import { useEffect, useState } from 'react';

const matches = (query: string): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches;

export const useMediaQuery = (query: string): boolean => {
  const [value, setValue] = useState(() => matches(query));
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = () => setValue(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return value;
};

// "Touch" here means the main pointer is a finger, not a mouse: phones and
// tablets. Used for wording ("Tap" vs "Click"/"Hover") and for interactions
// a mouse does differently (drag, hover). A laptop with a touchscreen still
// reports a fine pointer, so it keeps the desktop behaviour.
const TOUCH = '(pointer: coarse)';

export const isTouch = (): boolean => matches(TOUCH);

export const useIsTouch = (): boolean => useMediaQuery(TOUCH);

export type DeviceType = 'phone' | 'tablet' | 'computer';

// What kind of device this is, recorded on each attempt for Attempt History.
// iPads on iPadOS 13+ claim to be a Mac ("MacIntel") but have a touchscreen,
// which no Mac has. Otherwise a finger-driven device (coarse pointer, or a
// mobile user agent) is a phone when the shorter side of its screen is under
// 600px and a tablet above that, so holding it sideways changes nothing. A
// touchscreen laptop has a mouse/trackpad pointer and counts as a computer.
export const detectDeviceType = (): DeviceType => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'computer';
  const ua = navigator.userAgent || '';
  const iPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  if (iPadOS || /iPad/.test(ua)) return 'tablet';
  const mobileUa = /Mobi|Android|iPhone|iPod/.test(ua);
  if (!isTouch() && !mobileUa) return 'computer';
  const shortSide = Math.min(window.screen?.width || window.innerWidth, window.screen?.height || window.innerHeight);
  return shortSide < 600 ? 'phone' : 'tablet';
};
